import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient.tsx'
import {useTranslation} from '@/translations/i18n'
import {TreatmentRequestPatientValidationSchema} from '../../types'
import {FormCardSection} from '@/components/ui/form-atoms/FormAtoms'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {TreatmentRequestPatientPersonalData} from '@/features/treatment-requests/components/treatment-request-patient-personal-data/TreatmentRequestPatientPersonalData'
import {TreatmentRequestPatientUneligible} from '@/features/treatment-requests/components/treatment-request-patient-uneligible/TreatmentRequestPatientUneligible'
import {useEffect} from 'react'

export const TreatmentRequestPatientForm = ({onChangePatient}: {onChangePatient: () => void}) => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<TreatmentRequestPatientValidationSchema>()

    // form observer
    const patient = useWatch({control: form.control, name: 'patient'})

    // query
    const patientQuery = useGetPatient({
        patientId: patient?.id ?? undefined,
        responseType: 'extended',
        options: {
            enabled: false
        }
    })

    // side effects
    useEffect(() => {
        if (patientQuery.isSuccess) {
            form.setValue('project', patientQuery.data.project ?? null, {shouldDirty: true})
            form.setValue('serviceType', patientQuery.data.serviceType, {shouldDirty: true})
        }
    }, [patientQuery])

    return (
        <Grid columns={6} rowGap={4}>
            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="patient"
                    render={({field, fieldState}) => (
                        <InputSelectPatient
                            label={t('commons:name_header')}
                            placeholder={t('commons:select_patient')}
                            value={field.value}
                            errorMessage={fieldState.error?.message}
                            onChange={value => {
                                field.onChange({
                                    id: value?.id,
                                    fullName: value?.fullName
                                })
                                onChangePatient()
                            }}
                        />
                    )}
                />
            </GridItem>

            {patient && (
                <GridItem spanColumn={4}>
                    <FormCardSection columns={1} padding={3} rowGap={2}>
                        {patientQuery.isLoading ? (
                            <Flexbox direction="column" gap={2}>
                                <Skeleton height={18} />
                                <Flexbox gap={2}>
                                    <Flexbox direction="column" gap={2}>
                                        <Skeleton height={18} />
                                        <Skeleton height={18} />
                                    </Flexbox>
                                    <Flexbox direction="column" gap={2}>
                                        <Skeleton height={18} />
                                        <Skeleton height={18} />
                                    </Flexbox>
                                </Flexbox>
                            </Flexbox>
                        ) : patientQuery.isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : (
                            patientQuery.data &&
                            (patientQuery.data.treatmentRequestEligibility.canRequest ? (
                                <TreatmentRequestPatientPersonalData patient={patientQuery.data} />
                            ) : (
                                patientQuery.data.treatmentRequestEligibility.error?.code && (
                                    <TreatmentRequestPatientUneligible patient={patientQuery.data} />
                                )
                            ))
                        )}
                    </FormCardSection>
                </GridItem>
            )}
        </Grid>
    )
}
